import { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { collection, getDocs } from 'firebase/firestore';
import Navbar from '../components/Navbar';
import { useUser } from '../context/UserContext';

interface LeaderboardEntry {
  displayName: string;
  highScore: number;
  userId: string;
}

interface GameLeaderboard {
  [gameId: string]: LeaderboardEntry[];
}

const Leaderboard = () => {
  const { userName, stats } = useUser();
  const [leaderboards, setLeaderboards] = useState<GameLeaderboard>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLeaderboards = async () => {
      try {
        const usersRef = collection(db, 'users');
        const snapshot = await getDocs(usersRef);
        const gameScores: GameLeaderboard = {};

        snapshot.forEach((doc) => {
          const userData = doc.data();
          const userDisplayName = userData.displayName || userData.email.split('@')[0];
          
          // Process each game's high score
          Object.entries(userData.highScores || {}).forEach(([gameId, score]) => {
            if (!gameScores[gameId]) {
              gameScores[gameId] = [];
            }
            gameScores[gameId].push({
              displayName: userDisplayName,
              highScore: score as number,
              userId: doc.id
            });
          });
        });

        // Sort each game's leaderboard by score (descending)
        Object.keys(gameScores).forEach((gameId) => {
          gameScores[gameId].sort((a, b) => b.highScore - a.highScore);
        });

        setLeaderboards(gameScores);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching leaderboards:', error);
        setLoading(false);
      }
    };

    fetchLeaderboards();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 text-white">
        <Navbar userName={userName} displayName={stats?.displayName} />
        <div className="flex justify-center items-center h-[calc(100vh-100px)]">
          <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <Navbar userName={userName} displayName={stats?.displayName} />
      <div className="max-w-4xl mx-auto p-8">
        <h1 className="text-4xl font-bold mb-8 text-center">Leaderboards</h1>
        <div className="space-y-8">
          {Object.entries(leaderboards).map(([gameId, entries]) => (
            entries.length > 0 && (
              <div key={gameId} className="bg-slate-800 rounded-lg p-6 shadow-xl">
                <h2 className="text-2xl font-bold mb-4 text-indigo-400 capitalize">
                  {gameId} Leaderboard
                </h2>
                <div className="space-y-2">
                  {entries.map((entry, index) => (
                    <div 
                      key={entry.userId}
                      className={`flex justify-between items-center p-4 rounded-lg
                        ${index === 0 ? 'bg-yellow-500/10 text-yellow-100' : 
                          index === 1 ? 'bg-slate-400/10 text-slate-200' :
                          index === 2 ? 'bg-orange-500/10 text-orange-200' :
                          'bg-slate-700'}`}
                    >
                      <div className="flex items-center gap-4">
                        <span className="text-2xl font-bold w-8">{index + 1}</span>
                        <span className="font-semibold">{entry.displayName}</span>
                      </div>
                      <span className="text-xl">{entry.highScore}</span>
                    </div>
                  ))}
                </div>
              </div>
            )
          ))}
        </div>
      </div>
    </div>
  );
};

export default Leaderboard; 